import axios from "axios";
import { useCallback, useMemo } from "react";
import {
  MsgRedactedBase,
  MsgRedactedBatchCalls,
  MsgRedactedCall,
  MsgRedactedDepositToSubWallet,
  MsgRedactedGasTankWithdraw,
  MsgRedactedSwap,
  MsgRedactedWithdraw,
  MsgRedactedWithdrawFromSubWallet,
  Signer,
  Simulation,
  TxResult,
} from "rujira.js";
import { AccountProvider, proofDataToString, sha256 } from "rujira.ui";
import { REDACTED_BACKEND_URL } from "./config";
import { useRedactedProof } from ".";
import { MsgV2DirectExecute, useRedactedV2 } from "./v2";
import { useWalletAccounts } from "../services/accounts";
import type { V2PrivateFeeQuote } from "./v2/fees";
import {
  privateFeeQuoteFromSimulation,
  reviewedPrivateSimulation,
} from "./v2/reviewedSimulation";

/**
 * Legacy private transactions use a placeholder rather than chain simulation.
 * The commitment-based path instead requires an explicit reviewed fee quote.
 */
const REDACTED_SIMULATION: Simulation = {
  amount: BigInt(0),
  decimals: 8,
  gas: BigInt(0),
  symbol: "",
};

/**
 * Signs & broadcasts a MsgRedacted* transaction via the Redacted backend:
 * fetches the account nonce, generates the relevant zk proof and submits
 * the transaction to the corresponding backend endpoint.
 *
 * Thrown errors propagate to the shared TxButton, which surfaces them via
 * its standard toast / onError handling.
 */
const useRedactedSignAndBroadcast = () => {
  const { selected } = useWalletAccounts();
  const { generateProofMain: redactedProof, getNonce } = useRedactedProof(
    selected?.provider
  );
  const v2 = useRedactedV2();

  return useCallback(
    async (
      msg: MsgRedactedBase,
      reviewedQuote?: V2PrivateFeeQuote
    ): Promise<TxResult> => {
      if (v2.enabled) return v2.executePrivateIntent(msg, reviewedQuote);
      // Helper to submit a MsgRedactedBase transaction to the backend
      const submit = (
        msg: MsgRedactedBase,
        endpoint: string,
        errorMessage: string,
        additionalBody?: Record<string, any>
      ): Promise<TxResult> =>
        axios
          .post(`${REDACTED_BACKEND_URL}/${endpoint}`, {
            address: msg.account.address,
            stealth: msg.stealth,
            denom: msg.denom,
            amount: msg.amount,
            ...additionalBody,
          })
          .then((res) => {
            if (!res.data) {
              throw errorMessage;
            } else if (!res.data.ok) {
              throw res.data.error;
            } else {
              return {
                address: msg.account.address,
                network: msg.account.network,
                txHash: res.data.hash,
                deposited: null,
              };
            }
          });

      const nonce = await getNonce();
      if (!nonce) {
        throw "Failed to get Account Nonce";
      }
      if (msg instanceof MsgRedactedWithdraw) {
        const asset_str = `${msg.denom}:${msg.amount}`;
        const recipient = msg.account.address;
        const param = await sha256(nonce + recipient + asset_str);
        const proofData = await redactedProof(param);
        if (!proofData || !proofData.proof) {
          throw "Failed to generate proofData";
        }
        return submit(msg, "withdrawDenom", "Failed to withdraw", {
          proof: proofDataToString(proofData.proof),
          signature: proofData.signature.signature,
          pubkey: proofData.signature.pub_key.value,
          withdrawAmount: msg.amount,
          recipient: msg.account.address,
          param,
        });
      } else if (msg instanceof MsgRedactedGasTankWithdraw) {
        const recipient = msg.account.address;
        const param = await sha256(nonce + recipient + msg.amount);
        const proofData = await redactedProof(param);
        if (!proofData || !proofData.proof) {
          throw "Failed to generate proofData";
        }
        return submit(
          msg,
          "withdrawGasTank",
          "Failed to withdraw gas from gasTank",
          {
            proof: proofDataToString(proofData.proof),
            signature: proofData.signature.signature,
            pubkey: proofData.signature.pub_key.value,
            recipient: msg.account.address,
            param,
          }
        );
      } else if (msg instanceof MsgRedactedDepositToSubWallet) {
        const asset_str = `${msg.denom}:${msg.amount}`;
        const param = await sha256(nonce + asset_str);
        const proofData = await redactedProof(param);
        if (!proofData || !proofData.proof) {
          throw "Failed to generate proofData";
        }
        return submit(
          msg,
          "depositToSubWallet",
          "Failed to deposit to Private Account",
          {
            proof: proofDataToString(proofData.proof),
            signature: proofData.signature.signature,
            pubkey: proofData.signature.pub_key.value,
            param,
          }
        );
      } else if (msg instanceof MsgRedactedWithdrawFromSubWallet) {
        const asset_str = `${msg.denom}:${msg.amount}`;
        const param = await sha256(nonce + asset_str);
        const proofData = await redactedProof(param);
        if (!proofData || !proofData.proof) {
          throw "Failed to generate proofData";
        }
        return submit(
          msg,
          "withdrawFromSubWallet",
          "Failed to send To Private Reserve",
          {
            proof: proofDataToString(proofData.proof),
            signature: proofData.signature.signature,
            pubkey: proofData.signature.pub_key.value,
            param,
          }
        );
      } else if (msg instanceof MsgRedactedSwap) {
        const targetResponse = await axios.get(
          `${REDACTED_BACKEND_URL}/swapTarget`
        );
        const target = targetResponse.data?.target as string | undefined;
        if (!target) {
          throw "Failed to select a swap relayer";
        }
        const assetStr = `${msg.denom}:${msg.amount}`;
        const param = await sha256(nonce + target + assetStr);
        const proofData = await redactedProof(param);
        if (!proofData || !proofData.proof) {
          throw "Failed to generate proofData";
        }
        return submit(
          msg,
          "swapInSubWallet",
          "Failed to swap in Private Mode",
          {
            signature: proofData.signature.signature,
            pubkey: proofData.signature.pub_key.value,
            proof: proofDataToString(proofData.proof),
            param,
            nonce,
            target,
            memo: msg.memo,
            asset: msg.asset.asset,
            asset_type: msg.asset.type,
          }
        );
      } else if (msg instanceof MsgRedactedCall) {
        const asset_str =
          msg.denom && msg.amount ? `${msg.denom}:${msg.amount}` : "";
        const callJson = msg.call;
        const callBytes = new TextEncoder().encode(callJson);
        const callHex = [...callBytes]
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
        const param = await sha256(
          nonce + msg.contractAddress + callHex + asset_str
        );
        const proofData = await redactedProof(param);
        if (!proofData || !proofData.proof) {
          throw "Failed to generate proofData";
        }
        return submit(
          msg,
          "callToSubWallet",
          "Failed to call to Private Account in Private Mode",
          {
            proof: proofDataToString(proofData.proof),
            param,
            signature: proofData.signature.signature,
            pubkey: proofData.signature.pub_key.value,
            to_contract: msg.contractAddress,
            call: msg.call,
          }
        );
      } else if (msg instanceof MsgRedactedBatchCalls) {
        const calls = msg.calls.filter((call) => !!call);
        if (!calls || calls.length < 1) {
          throw "Failed to run contract";
        }
        const callBytes = new TextEncoder().encode(JSON.stringify(calls));
        const callHex = [...callBytes]
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");

        const param = await sha256(nonce + callHex);
        const proofData = await redactedProof(param);
        if (!proofData || !proofData.proof) {
          throw "Failed to generate proofData";
        }

        return submit(
          msg,
          "batchCallsToSubWallet",
          "Failed to batch calls to Private Account in Private Mode",
          {
            proof: proofDataToString(proofData.proof),
            signature: proofData.signature.signature,
            pubkey: proofData.signature.pub_key.value,
            param,
            calls: JSON.stringify(msg.calls),
          }
        );
      }
      throw "Unsupported private transaction";
    },
    [redactedProof, getNonce, v2]
  );
};

/**
 * Wraps an AccountProvider so that MsgRedacted* transactions are routed
 * through the Redacted backend instead of the wallet signer, while all
 * other Msgs use the default signer flow. This lets the shared rujira.ui
 * TxButton stay completely agnostic of privacy mode.
 */
export const useRedactedAccountProvider = (
  accountProvider: AccountProvider
): AccountProvider => {
  const signAndBroadcastRedacted = useRedactedSignAndBroadcast();
  const v2 = useRedactedV2();

  return useMemo(() => {
    const wrapSigner = (walletSigner: () => Signer): Signer => ({
      connect: () => walletSigner().connect(),
      simulate: async (tx) => {
        if (!(tx instanceof MsgRedactedBase))
          return walletSigner().simulate(tx);
        if (!v2.enabled) return REDACTED_SIMULATION;
        v2.assertSessionAccess();
        return reviewedPrivateSimulation(await v2.quotePrivateFees(tx));
      },
      signAndBroadcast: async (simulation, tx) => {
        // Recheck at the actual send boundary: a previously simulated button
        // can outlive the private session, including for a raw/nonprivate Msg.
        if (v2.enabled) v2.assertSessionAccess();
        if (tx instanceof MsgRedactedBase)
          return signAndBroadcastRedacted(
            tx,
            privateFeeQuoteFromSimulation(simulation)
          );
        const result = await walletSigner().signAndBroadcast(simulation, tx);
        if (tx instanceof MsgV2DirectExecute) {
          try {
            await tx.broadcasted(result);
          } catch {
            // The chain receipt is authoritative. A locked session or failed
            // balance refresh after submission must not invite a duplicate send.
          }
        }
        return result;
      },
      get onChange() {
        const inner = walletSigner();
        return inner.onChange?.bind(inner);
      },
      isAvailable: () => walletSigner().isAvailable(),
      get disconnect() {
        const inner = walletSigner();
        return inner.disconnect?.bind(inner);
      },
      networks: () => walletSigner().networks(),
      get signMessage() {
        const inner = walletSigner();
        return inner.signMessage?.bind(inner);
      },
      get requiresMessageSigningHandoff() {
        return walletSigner().requiresMessageSigningHandoff;
      },
      get messageSigningScheme() {
        return walletSigner().messageSigningScheme;
      },
    });

    return {
      ...accountProvider,
      signer: (address) => {
        // A restored V2 account is authorized by its private keys, not by the
        // original public wallet. Resolve a wallet signer only for wallet work;
        // never substitute the currently connected wallet for a missing signer.
        if (v2.enabled)
          return wrapSigner(() => accountProvider.signer(address));
        const inner = accountProvider.signer(address);
        return wrapSigner(() => inner);
      },
    };
  }, [accountProvider, signAndBroadcastRedacted, v2]);
};
